import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as echarts from 'echarts';

const defaultObject = {};
const defaultStyle = { height: '300px' };

export const EChartsRenderer = forwardRef(function EChartsRenderer(
    {
        option,
        className = null,
        style = defaultObject,
        showLoading = false,
        loadingOption = null,
        notMerge = false,
        lazyUpdate = false,
        theme = null,
        opts = defaultObject,
        onEvents = defaultObject,
        onChartReady = null
    },
    ref
) {
    const containerRef = useRef(null);
    const instanceRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
        const instance = echarts.init(containerRef.current, theme, opts);
        instanceRef.current = instance;

        if (onChartReady) {
            onChartReady(instance);
        }

        const observer = new ResizeObserver(() => {
            instance.resize();
        });
        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
            instance.dispose();
            instanceRef.current = null;
        };
        // theme/opts intentionally not in deps — changing them requires a remount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (instanceRef.current && option) {
            instanceRef.current.setOption(option, { notMerge, lazyUpdate });
        }
    }, [option, notMerge, lazyUpdate]);

    useEffect(() => {
        if (!instanceRef.current) {
            return;
        }
        if (showLoading) {
            instanceRef.current.showLoading(loadingOption);
        } else {
            instanceRef.current.hideLoading();
        }
    }, [showLoading, loadingOption]);

    useEffect(() => {
        const instance = instanceRef.current;
        if (!instance || !onEvents) {
            return undefined;
        }
        Object.entries(onEvents).forEach(([eventName, handler]) => {
            instance.on(eventName, handler);
        });
        return () => {
            Object.keys(onEvents).forEach((eventName) => {
                instance.off(eventName);
            });
        };
    }, [onEvents]);

    useImperativeHandle(ref, () => ({
        getEchartsInstance: () => instanceRef.current
    }));

    return (
        <div
            ref={containerRef}
            className={classNames('echarts-renderer', className)}
            style={{ ...defaultStyle, ...style }}
        />
    );
});

EChartsRenderer.propTypes = {
    option: PropTypes.object.isRequired,
    className: PropTypes.string,
    style: PropTypes.object,
    showLoading: PropTypes.bool,
    loadingOption: PropTypes.object,
    notMerge: PropTypes.bool,
    lazyUpdate: PropTypes.bool,
    theme: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    opts: PropTypes.object,
    onEvents: PropTypes.object,
    onChartReady: PropTypes.func
};
